/**
 * Date Utilities - Timezone-Safe Date Formatting
 * 
 * These utilities ensure dates are formatted correctly regardless of user's timezone.
 * Avoids the common pitfall of using toISOString() which converts to UTC.
 */

/**
 * Format a Date object as YYYY-MM-DD using local timezone
 * @param {Date} date - The date to format
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Get today's date as YYYY-MM-DD string in local timezone
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export const getTodayLocal = () => {
  return formatDateLocal(new Date());
};

/**
 * Add days to a date string (YYYY-MM-DD) and return new date string
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @param {number} days - Number of days to add (can be negative)
 * @returns {string} New date string in YYYY-MM-DD format
 */
export const addDaysToDateString = (dateStr, days) => {
  const parts = dateStr.split('-');
  const year = parseInt(parts[0]);
  const month = parseInt(parts[1]) - 1; // Month is 0-indexed
  const day = parseInt(parts[2]);
  
  const date = new Date(year, month, day);
  date.setDate(date.getDate() + days);
  
  return formatDateLocal(date);
};

/**
 * Parse a date string (YYYY-MM-DD) to a Date object at midnight local time
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {Date} Date object
 */
export const parseDateString = (dateStr) => {
  const parts = dateStr.split('-');
  const year = parseInt(parts[0]);
  const month = parseInt(parts[1]) - 1;
  const day = parseInt(parts[2]);
  return new Date(year, month, day);
};

/**
 * Check if a date string is today
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {boolean} True if date is today
 */
export const isToday = (dateStr) => {
  return dateStr === getTodayLocal();
};

/**
 * Check if date string is in the past
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {boolean} True if date is before today
 */
export const isPastDate = (dateStr) => {
  const date = parseDateString(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return date < today;
};

/**
 * Get ordinal suffix for a day number (1st, 2nd, 3rd, 4th, etc.)
 * @param {number} day - Day of month (1-31)
 * @returns {string} Ordinal suffix ('st', 'nd', 'rd', or 'th')
 */
export const getOrdinalSuffix = (day) => {
  if (day >= 11 && day <= 13) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

/**
 * Format a date string to human-readable format
 * "Jan 15th, 2026" instead of "2026-01-15"
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @param {object} options - Formatting options
 * @param {boolean} options.includeYear - Include year (default: true)
 * @param {boolean} options.includeDay - Include day of week (default: false)
 * @returns {string} Human-readable date string
 */
export const formatDateHuman = (dateStr, options = {}) => {
  if (!dateStr) return '';
  
  const { includeYear = true, includeDay = false } = options;
  
  // Parse the date string
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  
  const year = parseInt(parts[0]);
  const month = parseInt(parts[1]) - 1;
  const day = parseInt(parts[2]);
  const date = new Date(year, month, day);
  
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  const suffix = getOrdinalSuffix(day);
  const monthName = monthNames[month];
  
  let result = '';
  if (includeDay) {
    result = `${dayNames[date.getDay()]}, `;
  }
  result += `${monthName} ${day}${suffix}`;
  if (includeYear) {
    result += `, ${year}`;
  }
  
  return result;
};

/**
 * Format a time string to 12-hour format with AM/PM
 * "9:00 AM" instead of "09:00"
 * @param {string} timeStr - Time string in HH:MM format
 * @returns {string} Human-readable time string
 */
export const formatTimeHuman = (timeStr) => {
  if (!timeStr) return '';
  
  const parts = timeStr.split(':');
  if (parts.length < 2) return timeStr;
  
  let hour = parseInt(parts[0]);
  const minute = parts[1];
  const ampm = hour >= 12 ? 'PM' : 'AM';
  
  if (hour === 0) hour = 12;
  else if (hour > 12) hour -= 12;
  
  return `${hour}:${minute} ${ampm}`;
};

/**
 * Format a date and time together
 * "Jan 15th, 2026 at 9:00 AM"
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @param {string} timeStr - Time string in HH:MM format
 * @param {object} options - Formatting options
 * @returns {string} Combined human-readable string
 */
export const formatDateTimeHuman = (dateStr, timeStr, options = {}) => {
  const dateFormatted = formatDateHuman(dateStr, options);
  const timeFormatted = formatTimeHuman(timeStr);
  
  if (timeFormatted) {
    return `${dateFormatted} at ${timeFormatted}`;
  }
  return dateFormatted;
};
